import React, { useEffect, useState } from "react";
import { heatColor } from "../utils/heatColor";
import { gaugeGeometry } from "../utils/gaugeGeometry";
import { useRenderTier } from "../hooks/useRenderTier";

// De halve meter met het teampercentage. Van buiten naar binnen: een krans
// streepjes als schaalverdeling, daarbinnen de band met de voortgang, en op
// de kop een lichtpunt.
//
// Alles beweegt via CSS, niets per frame via JavaScript. Dat scheelt merkbaar
// op een scherm dat de hele dag aan staat.
//
// De gloed om de boog loopt via SVG-filters, en die zijn duur: een browser
// moet zo'n blur uitrekenen zodra er iets aan dat vlak verandert. Op een
// trage Pi gaat daar het grootste deel van de tijd in zitten, dus hoeveel
// gloed er is hangt af van de kwaliteitstrap (zie utils/renderTier.js):
//
//   full  - de wijde gloed, de strakke gloed en een zachte kop
//   light - alleen de strakke gloed om de band
//   off   - geen enkel filter; in plaats van de gloed staat er een brede,
//           doorschijnende band achter de boog. Van een afstand leest dat
//           bijna hetzelfde, en het kost niets.

const sweepEase = "1.4s cubic-bezier(0.22, 1, 0.36, 1)"; // snel weg, zacht landen

// Begin op 0 en spring pas een frame later naar de echte waarde. Dan heeft de
// animatie iets om vanaf te lopen en veegt de meter bij het openen open in
// plaats van meteen vol te staan.
function useSweepIn(target) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(target));
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return shown;
}

const ArcGauge = ({ percent = 0, size = 400, stroke = 26 }) => {
  const { fx } = useRenderTier();
  const glow = fx.gaugeGlow;
  const target = Math.min(100, Math.max(0, percent));
  const shown = useSweepIn(target);

  const progress = shown / 100;
  const { cx, cy, radius, height, path, length, dashOffset, ticks } =
    gaugeGeometry(size, stroke, progress);

  const hot = heatColor(shown);
  const mid = heatColor(shown * 0.72);
  const base = heatColor(18 + shown * 0.26);

  // Het kleurverloop loopt recht, de boog rond. Deze stop legt de heetste
  // kleur precies onder de kop, zodat de kleur de boog volgt.
  const tipStop = Math.max(0.02, (1 - Math.cos(Math.PI * progress)) / 2);

  // Kwadratisch: onderin blijft het rustig, richting target loopt het op.
  const heat = progress * progress;
  const bloomOpacity = 0.05 + 0.42 * heat;
  const glowOpacity = 0.2 + 0.22 * progress;
  const lit = target > 0.5;

  const sweep = { transition: `stroke-dashoffset ${sweepEase}` };
  const dash = {
    strokeDasharray: `${length} ${length}`,
    strokeDashoffset: dashOffset,
  };

  return (
    <svg
      className="gauge"
      width={size}
      height={height}
      viewBox={`0 0 ${size} ${height}`}
      style={{ overflow: "visible" }}
    >
      <defs>
        <linearGradient id="gaugeHeat" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={base} />
          <stop offset={tipStop * 0.55} stopColor={mid} />
          <stop offset={tipStop} stopColor={hot} />
          <stop offset="1" stopColor={hot} />
        </linearGradient>

        {/* Ruime filtervlakken: te krappe vlakken knippen de blur af en dat
            geeft zichtbare rechthoekige randen. */}
        <filter id="gaugeBloom" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={9 + 6 * progress} />
        </filter>
        <filter id="gaugeGlow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <filter id="gaugeHead" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation={5 + 5 * progress} />
        </filter>
      </defs>

      <g className="gauge-scale">
        {ticks.map((tick, i) => (
          <line
            key={i}
            className={`gauge-tick${tick.major ? " is-major" : ""}`}
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke={tick.color}
            strokeWidth={tick.width}
            strokeLinecap="round"
            style={{ transitionDelay: `${tick.delay}s` }}
          />
        ))}
      </g>

      {/* Lege baan */}
      <path
        d={path}
        fill="none"
        stroke="rgba(255, 255, 255, 0.14)"
        strokeWidth={stroke}
        strokeLinecap="round"
      />

      {/* Wijde gloed: volgt hetzelfde streepjespatroon als de voortgang, dus
          hij blijft op het gevulde stuk hangen. */}
      {lit && glow === "full" && (
        <path
          className="gauge-bloom"
          d={path}
          fill="none"
          stroke="url(#gaugeHeat)"
          strokeWidth={stroke + 22}
          strokeLinecap="round"
          filter="url(#gaugeBloom)"
          {...dash}
          style={{ "--flame": bloomOpacity, ...sweep }}
        />
      )}

      {/* Zonder filters: een brede doorschijnende band op de plek van de
          gloed. Geen blur, dus de browser tekent dit één keer. */}
      {lit && glow === "off" && (
        <path
          d={path}
          fill="none"
          stroke="url(#gaugeHeat)"
          strokeWidth={stroke + 16}
          strokeLinecap="round"
          opacity={0.16 + 0.12 * progress}
          {...dash}
          style={sweep}
        />
      )}

      {/* Strakke gloed vlak om de band */}
      {lit && glow !== "off" && (
        <path
          d={path}
          fill="none"
          stroke="url(#gaugeHeat)"
          strokeWidth={stroke + 7}
          strokeLinecap="round"
          filter="url(#gaugeGlow)"
          opacity={glowOpacity}
          {...dash}
          style={sweep}
        />
      )}

      {/* Voortgang */}
      <path
        d={path}
        fill="none"
        stroke="url(#gaugeHeat)"
        strokeWidth={stroke}
        strokeLinecap="round"
        {...dash}
        style={sweep}
      />

      {/* De kop draait om het middelpunt, zo loopt het lichtpunt over de boog
          mee in plaats van er in een rechte lijn overheen. */}
      {target > 1 && (
        <g
          className="gauge-headgroup"
          style={{
            transformBox: "view-box",
            transformOrigin: `${cx}px ${cy}px`,
            transform: `rotate(${180 * progress}deg)`,
            transition: `transform ${sweepEase}`,
          }}
        >
          <circle
            cx={cx - radius}
            cy={cy}
            r={stroke * 0.7}
            fill={hot}
            opacity={0.3 + 0.4 * heat}
            filter={glow === "off" ? undefined : "url(#gaugeHead)"}
          />
          <circle cx={cx - radius} cy={cy} r={stroke * 0.3} fill="#ffffff" />
        </g>
      )}

      <text className="gauge-cap" x={cx - radius} y={cy + 32} textAnchor="middle">
        0
      </text>
      <text className="gauge-cap" x={cx + radius} y={cy + 32} textAnchor="middle">
        100
      </text>
    </svg>
  );
};

export default ArcGauge;
